<template>
  <modal-template
    ref="modal"
    :title="title"
    type-modal="export_file_game_modal"
  >
    <div class="modal-content__main">
      <d-form>
        <d-form-row class="mb-4">
          <d-col cols="6">
            <d-checkbox
              class="custom-toggle-sm"
              v-for="(_, type) in model.selectedTypes"
              :key="type"
              v-model="model.selectedTypes[type]"
            >
              {{ model.typeName[type] }}
            </d-checkbox>
          </d-col>

          <d-col cols="6">
            <label>Formato do arquivo:</label>
            <div class="ml-2">
              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="svg"
                class="fs-6 text-muted"
              >
                SVG
              </d-radio>
              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="eps"
                class="fs-6 text-muted"
              >
                EPS
              </d-radio>
            </div>
          </d-col>
        </d-form-row>

        <d-form-row>
          <d-col cols="6" class="mb-2">
            <label>Estilo:</label>
            <d-checkbox toggle v-model="colored" class="custom-toggle-sm">
              <span class="mr-1">Colorido</span>
            </d-checkbox>
            <d-checkbox
              toggle
              v-model="transparentBackground"
              class="custom-toggle-sm"
            >
              <span class="mr-1">Fundo Tranparente</span>
            </d-checkbox>
          </d-col>

          <d-col cols="6" class="mb-3">
            <label>Espessura da linha:</label>
            <div class="ml-2">
              <d-radio
                v-model="exportPathWeight"
                name="path-weight"
                value="thin"
                class="mr-3 mb-0 fs-7"
              >
                Fina
              </d-radio>

              <d-radio
                v-model="exportPathWeight"
                name="path-weight"
                value="normal"
                class="mr-3 mb-0 fs-7"
              >
                Normal
              </d-radio>

              <d-radio
                v-model="exportPathWeight"
                name="path-weight"
                value="bold"
                class="mb-0 fs-7"
              >
                Espessa
              </d-radio>
            </div>
          </d-col>
        </d-form-row>
      </d-form>
    </div>
    <div slot="actions" class="modal-content__control">
      <d-button
        type="button"
        @click="onCancel()"
        class="btn btn-sm btn-secondary mr-2"
      >
        Cancelar
      </d-button>
      <d-button
        type="button"
        @click="onConfirm()"
        class="btn btn-sm btn-primary"
      >
        Confirmar
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { mapActions } from 'vuex';

import svgShapeLinksGenerator from '_utils/svgShapeLinksGenerator';
import { b64toBlob } from '_utils';
import ModalTemplate from '_shared/Modal.vue';
import ImageService from '_services/rest/modules/image';
import formatErrors from '_common/mixins/FormatErrors';

export default {
  name: 'export-file-modal',
  mixins: [formatErrors],
  components: { ModalTemplate },
  props: {
    rows: { type: Number, required: true },
    columns: { type: Number, required: true },
    table: { type: Array, required: false, default: () => [] },
    fileName: { type: String, required: true },
    settings: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      model: {
        selectedTypes: {
          enigma: true,
          solution: true,
        },
        typeName: {
          enigma: 'Enigma',
          solution: 'Solução',
        },
      },
      formatMode: 'eps',
      colored: false,
      transparentBackground: false,
      exportPathWeight: 'normal',
      title: 'Exportar arquivos',
    };
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert', 'showLoader', 'hideLoader']),
    onCancel() {
      this.setToggle({ type: 'export_file_game_modal', bool: false });
    },
    generateNumericId() {
      return `${Date.now()}${Math.floor(Math.random() * 100000)}`;
    },
    async downloadExportedFiles(zip, filenamePrefix) {
      const files = Object.entries(zip.files);

      if (files.length === 1) {
        const [fileName, zipEntry] = files[0];
        const blob = await zipEntry.async('blob');
        saveAs(blob, fileName);
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${filenamePrefix}.zip`);
    },
    processGameExport({
      table,
      rows,
      columns,
      baseName,
      svgList,
      selectedTypes,
      childSettings,
    }) {
      const exportConfig = {
        ...childSettings,
        colored: this.colored,
        pathWeight: this.exportPathWeight,
        transparentBackground: this.transparentBackground,
      };

      if (selectedTypes.enigma) {
        const svg = svgShapeLinksGenerator(table, rows, columns, {
          ...exportConfig,
          showAnswer: false,
        });
        svgList.push({ id: `${baseName}_Enigma`, svg });
      }

      if (selectedTypes.solution) {
        const svg = svgShapeLinksGenerator(table, rows, columns, {
          ...exportConfig,
          showAnswer: true,
        });
        svgList.push({ id: `${baseName}_Solucao`, svg });
      }
    },
    splitIntoBatches(items, batchSize) {
      const batches = [];
      for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
      }
      return batches;
    },
    prepareSvgFiles() {
      const { selectedTypes } = this.model;
      const svgFiles = [];
      const zipArchive = new JSZip();
      const exportId = this.generateNumericId();

      this.processGameExport({
        table: this.table,
        rows: this.rows,
        columns: this.columns,
        baseName: this.fileName,
        svgList: svgFiles,
        selectedTypes,
        childSettings: this.settings,
      });

      return { exportId, svgFiles, zipArchive };
    },
    processConvertedFile(file, zipArchive) {
      if (!file.epsBase64) return false;

      const blob = b64toBlob(file.epsBase64, 'application/postscript');
      zipArchive.file(`${file.id}.eps`, blob);
      return true;
    },
    convertSvgBatchToEps(svgFiles, zipArchive) {
      this.showLoader('Convertendo arquivos para EPS...');

      return ImageService.convertMultipleSVGsToEPS(svgFiles)
        .then(convertedFiles => {
          const allFilesOk = convertedFiles
            .map(file => this.processConvertedFile(file, zipArchive))
            .every(status => status === true);

          if (!allFilesOk) {
            this.activeAlert({
              message: 'Erro na conversão de alguns arquivos.',
              theme: 'danger',
            });
            this.onCancel();
            return false;
          }

          this.showLoader('Finalizando exportação');
          return true;
        })
        .catch(error => {
          this.activeAlert({
            message: this.formatErrors(error),
            theme: 'danger',
          });
          this.onCancel();
          return false;
        });
    },
    addSvgFilesToZip(svgFiles, zipArchive) {
      svgFiles.forEach(file => {
        zipArchive.file(`${file.id}.svg`, file.svg);
      });
    },
    async finalizeExport(zipArchive, exportId) {
      await this.downloadExportedFiles(
        zipArchive,
        `${this.fileName}-${exportId}`,
      );
      this.onCancel();
    },
    async exportSvgFiles() {
      const { exportId, svgFiles, zipArchive } = this.prepareSvgFiles();

      if (svgFiles.length > 0) {
        if (this.formatMode === 'eps') {
          const success = await this.convertSvgBatchToEps(svgFiles, zipArchive);
          if (!success) return;
        } else {
          this.addSvgFilesToZip(svgFiles, zipArchive);
        }
      }

      await this.finalizeExport(zipArchive, exportId);
      this.hideLoader();
    },
    onConfirm() {
      this.exportSvgFiles();
    },
  },
};
</script>
